import React, { useState, useEffect } from 'react';

import ShowHideOptionsLink from './ShowHideOptionsLink';
import SelectedTermsCounter from './SelectedTermsCounter';

interface Term {
  label: string;
  value: string;
}

interface ButtonsFilterProps {
  initialItems: Term[];
  label: string;
  maxButtonsToShow?: number;
  selectedItems: string[];
  setSelectedItems: React.Dispatch<React.SetStateAction<string[]>>;
  showCounter?: boolean;
}

const haveHiddenPreSelectedItems = (
  initialItems: Term[],
  maxButtonsToShow: number
): boolean => {
  if (!window.location.search) return false;
  if (initialItems.length <= maxButtonsToShow) return false;

  const hiddenItems = initialItems
    .slice(maxButtonsToShow)
    .map((item) => item.value);

  return hiddenItems.some((item) => window.location.search.includes(item));
};

const FilterButtons: React.FC<ButtonsFilterProps> = ({
  initialItems,
  label,
  maxButtonsToShow,
  selectedItems,
  setSelectedItems,
  showCounter = false,
}: ButtonsFilterProps) => {
  const [terms, setTerms] = useState<Term[]>(initialItems || []);
  const [itemsToShow, setItemsToShow] = useState<number>(0);

  const showLessTopicsBtn = maxButtonsToShow && terms.length > maxButtonsToShow;

  const handleFilterClick = (term: Term) => {
    setSelectedItems((prevFilters) =>
      prevFilters.includes(term.value)
        ? prevFilters.filter((f) => f !== term.value)
        : [...prevFilters, term.value]
    );
  };

  const toggleShowAllOrSome = () => {
    if (!maxButtonsToShow || isNaN(maxButtonsToShow)) return;
    setItemsToShow(
      itemsToShow === terms.length ? maxButtonsToShow : terms.length
    );
  };

  useEffect(() => {
    setTerms([...initialItems]);
  }, [initialItems]);

  useEffect(() => {
    const hasHiddenItems = haveHiddenPreSelectedItems(terms, maxButtonsToShow);

    const newItemsToShow =
      hasHiddenItems === true
        ? terms.length
        : maxButtonsToShow < terms.length
          ? maxButtonsToShow
          : terms.length;

    setItemsToShow(newItemsToShow);
  }, [terms, maxButtonsToShow]);

  return (
    <div className="filter_buttons">
      <div className="flex flex-wrap gap-[8px] items-center">
        <div className="font-bold leading-[16.03px] text-[14px] lg:w-auto w-full">
          {label}
        </div>
        {showCounter && selectedItems.length > 0 && (
          <div className="hidden lg:flex gap-[8px] items-center">
            <span className="text-[16px]">|</span>
            <SelectedTermsCounter total={selectedItems.length} />
          </div>
        )}
        {terms.map((term, index) => {
          const statusClass = selectedItems.includes(term.value)
            ? 'active bg-[#3722D3] text-white'
            : 'bg-white text-gray-800 hover:border-[#3722D3] hover:text-[#3722D3] hover:bg-white';

          if (index >= itemsToShow) {
            return null;
          }

          return (
            <button
              key={index + term.value}
              className={`filter_buttons__button cursor-pointer py-[8px] px-[16px] rounded-[50px] border border-[#DCDEE1] font-size-[14px] font-normal leading-[22.4px] transition-colors ${statusClass}`}
              onClick={() => {
                handleFilterClick(term);
              }}
            >
              {term.label}
            </button>
          );
        })}
        {showLessTopicsBtn && (
          <ShowHideOptionsLink
            showingAll={itemsToShow === terms.length}
            styles="hidden lg:inline"
            onClick={toggleShowAllOrSome}
          />
        )}
      </div>
    </div>
  );
};

export default FilterButtons;
